// ===============================================================
// nativeBottomNavRoutePolicy.js — pure, ordered route -> shell-state
// policy for the Android native bottom-navigation shell.
// ===============================================================
//
// Mirrors current production behavior EXACTLY, as proven by the 8 pages
// that render <BottomNav active="..."> today (BottomNav.js's own
// consumers - HomePage, MyProfilePage, MyRankRewardsPage,
// FemaleJourneyRewardsPage, InvitePage, MessagesPage, FriendsPage,
// NotificationsPage) and by the fact that no other page in src/App.js's
// route tree renders it at all. Every other route resolves to
// {activeTab: null, barVisible: false}.
//
// Uses react-router-dom's own matchPath (not startsWith) so a dynamic
// segment like /user/:id or /chat/:chatId can never be confused with an
// unrelated static route sharing a path prefix. Patterns are matched in
// order, first match wins - there is no overlap/ambiguity in this app's
// actual route set today, but ordering is still explicit and intentional
// (static routes before their potential dynamic neighbors) rather than
// relied upon implicitly.

import { matchPath } from "react-router-dom";

export const TAB_HOME = "home";
export const TAB_MESSAGES = "messages";
export const TAB_CAMERA = "camera";
export const TAB_LIVE = "live";
export const TAB_NOTIFICATIONS = "notifications";
export const TAB_PROFILE = "profile";

const HIDDEN = { activeTab: null, barVisible: false, fullScreenSuppressed: false };
const HIDDEN_FULL_SCREEN = { activeTab: null, barVisible: false, fullScreenSuppressed: true };

function visible(activeTab) {
  return { activeTab, barVisible: true, fullScreenSuppressed: false };
}

// [pattern, result] - first match wins.
const ROUTE_POLICY_TABLE = [
  ["/home", visible(TAB_HOME)],
  ["/my-profile", visible(TAB_PROFILE)],
  ["/my-rank-rewards", visible(TAB_PROFILE)],
  ["/female-journey-rewards", visible(TAB_PROFILE)],
  ["/invite", visible(TAB_PROFILE)],
  ["/messages", visible(TAB_MESSAGES)],
  ["/friends", visible(TAB_MESSAGES)],
  ["/notifications", visible(TAB_NOTIFICATIONS)], // web fallback route only

  // Everything below is hidden today - enumerated explicitly (never a
  // broad startsWith) so a future new route defaults to the true
  // catch-all below rather than silently inheriting a sibling's policy.
  ["/user/:id", HIDDEN],
  ["/edit-profile", HIDDEN],
  ["/online", HIDDEN],
  ["/beauty", HIDDEN_FULL_SCREEN],
  ["/snap-camera", HIDDEN_FULL_SCREEN], // camera destination - no selected state where hidden
  ["/chat-settings", HIDDEN],
  ["/chat/:chatId", HIDDEN_FULL_SCREEN],
  ["/chat/new/:chatId", HIDDEN_FULL_SCREEN],
  ["/buy-coins", HIDDEN],
  ["/female-earnings", HIDDEN],
  ["/withdraw-request", HIDDEN],
  ["/withdraw-confirm/:id", HIDDEN],
  ["/withdraw-history", HIDDEN],
  ["/convert-earnings-to-coins", HIDDEN],
  ["/withdraw-help", HIDDEN],
  ["/call-outgoing/:callId", HIDDEN_FULL_SCREEN],
  ["/call/:callId", HIDDEN_FULL_SCREEN],
  ["/call-native/:callId", HIDDEN_FULL_SCREEN],
  ["/match-radar", HIDDEN_FULL_SCREEN], // live destination - no selected state where hidden
  ["/my-level", HIDDEN],
  ["/fe/chat-price", HIDDEN],
  ["/premium", HIDDEN],
  ["/premium/vpn", HIDDEN],
  // Settings: exact known sub-routes only, never a wildcard.
  ["/settings", HIDDEN],
  ["/settings/account", HIDDEN],
  ["/settings/blocklist", HIDDEN],
  ["/settings/help", HIDDEN],
  ["/settings/language", HIDDEN],
  ["/settings/change-phone", HIDDEN],
  ["/settings/change-email", HIDDEN],
  ["/settings/change-password", HIDDEN],
  ["/settings/delete-account", HIDDEN],
  ["/support/new/:type", HIDDEN],
  ["/support/history", HIDDEN],
  ["/support/ticket/:ticketId", HIDDEN_FULL_SCREEN],
  ["/privacy", HIDDEN],
  ["/terms", HIDDEN],
  ["/", HIDDEN_FULL_SCREEN],
  ["/select-gender", HIDDEN_FULL_SCREEN],
  ["/verification", HIDDEN_FULL_SCREEN],
  ["/verification-info", HIDDEN_FULL_SCREEN],
  ["/pending-delete", HIDDEN_FULL_SCREEN],
  ["/account-suspended", HIDDEN_FULL_SCREEN],
  ["/admin1/*", HIDDEN_FULL_SCREEN],
];

/**
 * @param {string} pathname current location.pathname (e.g. from
 *     react-router-dom's useLocation()).
 * @returns {{activeTab: string|null, barVisible: boolean, fullScreenSuppressed: boolean}}
 *     Unmatched paths (including any future/unknown/404 route) resolve
 *     to the hidden, no-active-tab default - never inherited from a
 *     sibling pattern.
 */
export function resolveRoutePolicy(pathname) {
  for (const [pattern, result] of ROUTE_POLICY_TABLE) {
    if (matchPath({ path: pattern, end: true }, pathname)) {
      return result;
    }
  }
  return HIDDEN;
}
